import * as Yup from 'yup';

import { UploadTrigger } from './upload';

// Event types must match the Go EventType constants in
// lambdas/api/models/event.go. Both server-fired milestones and client-fired
// engagement events are listed.
export const EventTypes = [
  'signup',
  'trial_converted',
  'vendor_integration',
  'store_upload',
  'order_sent',
  'order_edited',
  'receiving_completed',
  'buyup_engaged',
  'exception_logs_engaged',
  'sales_journal_engaged',
  'mcp_tool_used',
] as const;

export type EventType = (typeof EventTypes)[number];

// Per-event-type payload schemas mirror the Go *Payload structs in
// lambdas/api/services/events.go. Keep these in sync field-for-field.
const EmptyPayloadSchema = Yup.object().shape({}).default({});

const VendorIntegrationPayloadSchema = Yup.object().shape({
  vendor_id: Yup.string().required(),
  vendor_name: Yup.string().required(),
  vendor_normalized_name: Yup.string().optional(),
});

const StoreUploadPayloadSchema = Yup.object().shape({
  trigger: Yup.mixed<UploadTrigger>().oneOf(Object.values(UploadTrigger)).required(),
  product_count: Yup.number().integer().min(0).required(),

  enabled_products_count: Yup.number().integer().min(0).default(0),
  negative_qoh_count: Yup.number().integer().min(0).default(0),
  products_sold_365_count: Yup.number().integer().min(0).default(0),
  priced_product_count: Yup.number().integer().min(0).default(0),
  priced_item_code_count: Yup.number().integer().min(0).default(0),
  suggested_item_code_count: Yup.number().integer().min(0).default(0),
});

const OrderSentPayloadSchema = Yup.object().shape({
  order_id: Yup.string().required(),
  vendor_id: Yup.string().required(),
  vendor_normalized_name: Yup.string().optional(),
  item_count: Yup.number().required(),
  total_cents: Yup.number().required(),
  total_discount_cents: Yup.number().required(),
});

const OrderEditedPayloadSchema = Yup.object().shape({
  order_id: Yup.string().required(),
  edit_count: Yup.number().required(),
});

// vendor_id and vendor_normalized_name were added to receiving_completed in
// this branch — old events have an empty payload and both fields absent.
const ReceivingCompletedPayloadSchema = Yup.object().shape({
  vendor_id: Yup.string().optional(),
  vendor_normalized_name: Yup.string().optional(),
});

const BuyupEngagedPayloadSchema = Yup.object().shape({
  order_id: Yup.string().required(),
  vendor_id: Yup.string().required(),
  vendor_normalized_name: Yup.string().optional(),
});

const MCPToolUsedPayloadSchema = Yup.object().shape({
  tool: Yup.string().required(),
});

export type VendorIntegrationPayload = Yup.InferType<typeof VendorIntegrationPayloadSchema>;
export type StoreUploadPayload = Yup.InferType<typeof StoreUploadPayloadSchema>;
export type OrderSentPayload = Yup.InferType<typeof OrderSentPayloadSchema>;
export type OrderEditedPayload = Yup.InferType<typeof OrderEditedPayloadSchema>;
export type ReceivingCompletedPayload = Yup.InferType<typeof ReceivingCompletedPayloadSchema>;
export type BuyupEngagedPayload = Yup.InferType<typeof BuyupEngagedPayloadSchema>;
export type MCPToolUsedPayload = Yup.InferType<typeof MCPToolUsedPayloadSchema>;

const baseEventShape = {
  store_id: Yup.string().required(),
  event_id: Yup.string().required(),
  created_at: Yup.date().required(),
};

const SignupEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'signup'>()
    .oneOf(['signup'] as const)
    .required(),
  payload: EmptyPayloadSchema.nullable(),
});

const TrialConvertedEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'trial_converted'>()
    .oneOf(['trial_converted'] as const)
    .required(),
  payload: EmptyPayloadSchema.nullable(),
});

const VendorIntegrationEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'vendor_integration'>()
    .oneOf(['vendor_integration'] as const)
    .required(),
  payload: VendorIntegrationPayloadSchema.required(),
});

const StoreUploadEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'store_upload'>()
    .oneOf(['store_upload'] as const)
    .required(),
  payload: StoreUploadPayloadSchema.required(),
});

const OrderSentEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'order_sent'>()
    .oneOf(['order_sent'] as const)
    .required(),
  payload: OrderSentPayloadSchema.required(),
});

const OrderEditedEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'order_edited'>()
    .oneOf(['order_edited'] as const)
    .required(),
  payload: OrderEditedPayloadSchema.required(),
});

const ReceivingCompletedEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'receiving_completed'>()
    .oneOf(['receiving_completed'] as const)
    .required(),
  payload: ReceivingCompletedPayloadSchema.required(),
});

const BuyupEngagedEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'buyup_engaged'>()
    .oneOf(['buyup_engaged'] as const)
    .required(),
  payload: BuyupEngagedPayloadSchema.required(),
});

const ExceptionLogsEngagedEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'exception_logs_engaged'>()
    .oneOf(['exception_logs_engaged'] as const)
    .required(),
  payload: EmptyPayloadSchema.nullable(),
});

const SalesJournalEngagedEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'sales_journal_engaged'>()
    .oneOf(['sales_journal_engaged'] as const)
    .required(),
  payload: EmptyPayloadSchema.nullable(),
});

const MCPToolUsedEventSchema = Yup.object().shape({
  ...baseEventShape,
  event_type: Yup.string<'mcp_tool_used'>()
    .oneOf(['mcp_tool_used'] as const)
    .required(),
  payload: MCPToolUsedPayloadSchema.required(),
});

export type SignupEvent = Yup.InferType<typeof SignupEventSchema>;
export type TrialConvertedEvent = Yup.InferType<typeof TrialConvertedEventSchema>;
export type VendorIntegrationEvent = Yup.InferType<typeof VendorIntegrationEventSchema>;
export type StoreUploadEvent = Yup.InferType<typeof StoreUploadEventSchema>;
export type OrderSentEvent = Yup.InferType<typeof OrderSentEventSchema>;
export type OrderEditedEvent = Yup.InferType<typeof OrderEditedEventSchema>;
export type ReceivingCompletedEvent = Yup.InferType<typeof ReceivingCompletedEventSchema>;
export type BuyupEngagedEvent = Yup.InferType<typeof BuyupEngagedEventSchema>;
export type ExceptionLogsEngagedEvent = Yup.InferType<typeof ExceptionLogsEngagedEventSchema>;
export type SalesJournalEngagedEvent = Yup.InferType<typeof SalesJournalEngagedEventSchema>;
export type MCPToolUsedEvent = Yup.InferType<typeof MCPToolUsedEventSchema>;

export type Event =
  | SignupEvent
  | TrialConvertedEvent
  | VendorIntegrationEvent
  | StoreUploadEvent
  | OrderSentEvent
  | OrderEditedEvent
  | ReceivingCompletedEvent
  | BuyupEngagedEvent
  | ExceptionLogsEngagedEvent
  | SalesJournalEngagedEvent
  | MCPToolUsedEvent;

const EVENT_SCHEMAS = {
  signup: SignupEventSchema,
  trial_converted: TrialConvertedEventSchema,
  vendor_integration: VendorIntegrationEventSchema,
  store_upload: StoreUploadEventSchema,
  order_sent: OrderSentEventSchema,
  order_edited: OrderEditedEventSchema,
  receiving_completed: ReceivingCompletedEventSchema,
  buyup_engaged: BuyupEngagedEventSchema,
  exception_logs_engaged: ExceptionLogsEngagedEventSchema,
  sales_journal_engaged: SalesJournalEngagedEventSchema,
  mcp_tool_used: MCPToolUsedEventSchema,
} as const;

// Yup.lazy dispatches to the per-event-type schema by reading `event_type`.
// Unknown types fall through to the permissive base shape so an unrecognized
// event from a newer backend doesn't blow up the whole timeline.
export const EventSchema = Yup.lazy((value) => {
  const t = (value as { event_type?: string } | undefined)?.event_type as EventType | undefined;
  if (t && t in EVENT_SCHEMAS) {
    return EVENT_SCHEMAS[t];
  }

  return Yup.object().shape({
    ...baseEventShape,
    event_type: Yup.string()
      .oneOf([...EventTypes])
      .required(),
    payload: Yup.mixed().nullable(),
  });
});

// EventSchema is a Yup.lazy, which Yup's static types can't propagate into the
// containing schema — `Yup.InferType` of the result would widen to unknown[].
// We cast to ObjectSchema<OnboardingStoreEvents> so consumers (admin service,
// timeline page) get the discriminated-union type at the validation boundary.
export type OnboardingStoreEvents = {
  events: Event[];
};

export const OnboardingStoreEventsSchema = Yup.object().shape({
  events: Yup.array().of(EventSchema).required(),
}) as unknown as Yup.ObjectSchema<OnboardingStoreEvents>;
